import { PropsWithChildren, useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

type StaggeredScrollRevealProps = PropsWithChildren<{
  className?: string;
  /** Selector for each item to reveal in sequence */
  itemSelector?: string;
  stagger?: number;
  start?: string;
}>;

/** Reveals child items one after another when scrolled into view. */
export default function StaggeredScrollReveal({
  children,
  className,
  itemSelector = '[data-stagger-item]',
  stagger = 0.14,
  start = 'top 82%',
}: StaggeredScrollRevealProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useGSAP(
    () => {
      const root = rootRef.current;
      if (!root) return;

      const items = root.querySelectorAll<HTMLElement>(itemSelector);
      if (!items.length) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reducedMotion) {
        gsap.set(items, { opacity: 1, y: 0, clearProps: 'opacity,transform' });
        return;
      }

      gsap.set(items, { opacity: 0, y: 22 });

      const playReveal = () => {
        gsap.to(items, {
          opacity: 1,
          y: 0,
          duration: 0.55,
          ease: 'power2.out',
          stagger,
          overwrite: true,
        });
      };

      let played = false;
      const triggerReveal = () => {
        if (played) return;
        played = true;
        playReveal();
      };

      const scrollTrigger = ScrollTrigger.create({
        trigger: root,
        start,
        onEnter: triggerReveal,
        onEnterBack: triggerReveal,
      });

      if (ScrollTrigger.isInViewport(root)) {
        triggerReveal();
      }

      return () => scrollTrigger.kill();
    },
    { scope: rootRef, dependencies: [itemSelector, stagger, start] },
  );

  return (
    <div ref={rootRef} className={className}>
      {children}
    </div>
  );
}
